import classNames from 'classnames';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, useField } from 'formik';
import React from 'react';
import { useFormSettingsContext } from '../../Form.context';
import FormFooter from './components/form-footer';
import FormHeader from './components/form-header';
import { fieldClasses, FormFieldSC } from './FormField.styled';

type TFormHelpers = {
  field: FieldInputProps<any>;
  meta: FieldMetaProps<any>;
  helpers: FieldHelperProps<any>;
};

export interface FormFieldProps {
  /** Name of input in form */
  name: string;
  /** Name of prop to which value will be injected */
  valuePropName?: string;
  /** Custom value extractor from onChange event */
  getValueFromEvent?: (...args: any[]) => any;
  /** Error to show in form */
  error?: string | boolean;
  /** Remove styles from item wrapper */
  noStyle?: boolean;
  /**
   * input component
   */
  children: React.ComponentElement<any, any> | ((fieldProps: TFormHelpers) => React.ReactNode);
  /** Custom class name of item */
  className?: string;
  /** Custom style for item */
  style?: React.CSSProperties;
  label?: string;
  required?: boolean;
  showFeedback?: boolean;
  caption?: string;
  showError?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  /**
   * Show counter with max length in footer.
   * Use only with types with `.length`
   */
  maxLength?: number;
  tooltip?: string;
  showLabel?: boolean;
}

const FormField: React.FC<FormFieldProps> = ({
  children,
  className,
  style,
  error,
  noStyle,
  name,
  valuePropName = 'value',
  getValueFromEvent,
  label,
  required,
  showFeedback,
  caption,
  showError = true,
  maxLength,
  disabled,
  readonly,
  tooltip,
  showLabel,
}) => {
  const settings = useFormSettingsContext({
    showFeedback,
    disabled,
    showLabel,
  });

  const ref = React.useRef<HTMLDivElement>(null);
  const [field, meta, helpers] = useField({ name });
  const hasError = name ? Boolean(error) || (meta.error && meta.touched) : false;
  const finalError = error || meta.error;
  const fieldStatus = meta.touched ? (hasError ? 'error' : 'success') : undefined;
  const hasHeader = Boolean(
    (label && settings.showLabel) || required || settings.showFeedback || tooltip,
  );

  return (
    <FormFieldSC
      style={style}
      className={classNames(
        fieldClasses.wrapper,
        className,
        fieldStatus ? `${fieldClasses.wrapper}-${fieldStatus}` : undefined,
      )}
      noStyle={noStyle}
      hasHeader={hasHeader}
      ref={ref}
    >
      {hasHeader ? (
        <FormHeader
          required={required}
          status={fieldStatus}
          label={label}
          showFeedback={settings.showFeedback}
          name={name}
          tooltip={tooltip}
        />
      ) : label && !settings.showLabel ? (
        <label htmlFor={name} style={{ display: 'none' }}>
          {label}
        </label>
      ) : null}
      <div className={classNames(fieldClasses.content)}>
        {typeof children === 'function'
          ? children({ field, meta, helpers })
          : React.cloneElement(children, {
              [valuePropName]: field.value,
              onChange: getValueFromEvent
                ? (e: any) => {
                    children.props?.onChange?.(e);
                    helpers.setValue(getValueFromEvent(e));
                  }
                : (e: any) => {
                    children.props?.onChange?.(e);
                    field.onChange(e);
                  },
              onBlur: (e: any) => {
                children.props?.onBlur?.(e);
                field.onBlur(e);
              },
              error: children.props.error ?? hasError,
              name,
              id: name,
              required: children.props.required ?? required,
              disabled: children.props.disabled ?? settings.disabled,
              readonly: children.props.readonly ?? readonly,
            })}
      </div>
      <FormFooter
        caption={caption}
        error={hasError && showError ? finalError : undefined}
        maxLength={maxLength}
        currentLength={
          maxLength
            ? field.value && field.value.hasOwnProperty('length')
              ? field.value.length
              : 0
            : 0
        }
      />
    </FormFieldSC>
  );
};

export default FormField;
